export type SortDirection = 'ASC' | 'DESC'
export type SortField = 'name' | 'status' | 'last_activity' | 'last_scan'

export interface SortDescriptor {
  field: string
  direction: SortDirection
}

export const normalizeStatus = (value: any): string => String(value ?? '').trim().toLowerCase()

export const getCertificateValue = (cert: any, field: string): any => {
  switch (field) {
    case 'name': 
      return cert?.product?.title ?? ''
    case 'status': 
      return cert?.status ?? ''
    case 'owned_since': {
      /*
       * The same value the certificate page shows as "Owned since", with the same
       * fallback — `date_of_ownership` is null on certificates that were never
       * transferred, where the date you have owned it since IS its creation.
       * Sorting them as null would bucket every original-owner certificate
       * together at one end, which is the opposite of what the sort is for.
       */
      const raw: any = cert?.date_of_ownership ?? cert?.created_at
      if (raw == null) return ''
      const num = typeof raw === 'number' ? raw : Number(raw)
      const epochMs = isNaN(num) ? Date.parse(String(raw)) : (num < 1e12 ? num * 1000 : num)
      return epochMs || ''
    }
    case 'last_activity': {
      const raw: any = cert?.timestamp
      if (raw == null) return ''
      const num = typeof raw === 'number' ? raw : Number(raw)
      const epochMs = isNaN(num) ? Date.parse(String(raw)) : (num < 1e12 ? num * 1000 : num)
      return epochMs || ''
    }
    default: 
      return ''
  }
}

export const parseTimestamp = (raw: any): Date | null => {
  if (raw == null) return null
  const num = typeof raw === 'number' ? raw : Number(raw)
  const tsMs = isNaN(num) ? Date.parse(String(raw)) : (num < 1e12 ? num * 1000 : num)
  return isNaN(tsMs) ? null : new Date(tsMs)
}

export const filterByDateRange = (
  certificates: any[], 
  from?: string, 
  to?: string
): any[] => {
  if (!from && !to) return certificates
  
  const fromDate = from ? new Date(from) : null
  const toDate = to ? new Date(to) : null
  
  return certificates.filter(cert => {
    const timestamp = parseTimestamp(cert?.timestamp)
    if (!timestamp) return false
    
    if (fromDate && timestamp < fromDate) return false
    if (toDate && timestamp > toDate) return false
    return true
  })
}

export const filterBySelectedIds = (certificates: any[], selectedIds?: string): any[] => {
  if (!selectedIds) return certificates
  
  const ids = selectedIds.split(',')
  return certificates.filter(cert => ids.includes(cert.id))
}

export const compareValues = (a: any, b: any, direction: SortDirection): number => {
  if (a == null && b == null) return 0
  if (a == null) return direction === 'ASC' ? -1 : 1
  if (b == null) return direction === 'ASC' ? 1 : -1
  if (a < b) return direction === 'ASC' ? -1 : 1
  if (a > b) return direction === 'ASC' ? 1 : -1
  return 0
}

export const sortCertificates = (certificates: any[], descriptors: SortDescriptor[]): any[] => {
  if (!descriptors.length) return certificates

  const compare = (a: any, b: any): number => {
    for (const { field, direction } of descriptors) {
      const va = getCertificateValue(a, field)
      const vb = getCertificateValue(b, field)
      const result = compareValues(va, vb, direction)
      if (result !== 0) return result
    }
    return 0
  }

  return [...certificates].sort(compare)
}

/**
 * VERA-304 — the certificate statuses that mean "a transfer is in flight".
 * Terminal ones (success / failed / canceled) are deliberately absent: after a
 * successful claim the recipient simply owns the row, and a canceled transfer
 * must drop out rather than linger.
 */
export const IN_FLIGHT_TRANSFER_STATUSES = [
  'transfer_initiated',
  'transfer_waiting_confirmation',
] as const;

/**
 * Is this in-flight transfer addressed TO the signed-in user (i.e. incoming)?
 *
 * The id comparison is deliberately tolerant. Per the VERA-837 note in the API's
 * UpdateCustomerProfileUseCase, a session's id can be EITHER an `owners.id` or a
 * `users.id` depending on which login minted the token, and the certificate
 * serializer can return either shape for `toCustomer`. Comparing only one of
 * them silently misclassifies a real recipient as a sender — which is exactly
 * how the gift card showed "Transfer out" to the person receiving it.
 *
 * Extracted here because this is the SECOND consumer (§36): StatusNotification
 * decides the card's copy with it, PendingTransferNotification decides whether
 * to pin the gift to the top. Those two must never disagree about who the
 * recipient is.
 */
export const isIncomingTransferFor = (
  toCustomer: {id?: string} | null | undefined,
  profile: {id?: string; userId?: string} | null | undefined,
): boolean =>
  Boolean(
    toCustomer?.id &&
      profile &&
      (profile.userId === toCustomer.id || profile.id === toCustomer.id),
  );

/**
 * VERA-304 (A4) — the statuses of a TRANSFER RECORD that mean "still in flight".
 *
 * Note what this is NOT: `IN_FLIGHT_TRANSFER_STATUSES` above lists CERTIFICATE
 * statuses, and the two are different vocabularies for the same journey. That
 * distinction is not academic — it was a live bug. A gift's certificate walks
 * `transfer_initiated` -> `transfer_waiting_confirmation` -> `waiting_accept`,
 * and `waiting_accept` is where it SITS while the recipient is being waited on.
 * The certificate-status list omits it, so anything gating on that list saw
 * nothing for a gift in the only state a recipient ever observes it in
 * (verified against the dev database: every in-flight transfer there is
 * `waiting_accept` / `confirmation`, 4 of 4).
 *
 * These four values are the ones the API's own list query admits an incoming
 * transfer by (`certificate.drizzle.repository.ts`, the customer WHERE clause
 * and the ORDER BY pin that mirrors it). Matching it EXACTLY is the point: the
 * server decides which rows are on page 1 and in what order, so a client
 * predicate that disagreed would flag a row the server did not pin, or miss one
 * it did.
 */
export const IN_FLIGHT_TRANSFER_RECORD_STATUSES = [
  'initiated',
  'scanned',
  'pending',
  'confirmation',
] as const;

/**
 * VERA-304 (A4) — is this row an incoming transfer to the signed-in user that
 * has not settled yet? The client-side mirror of the server's membership rule.
 */
export const isIncomingInFlightTransfer = (
  certificate: {transfer?: {status?: string; toCustomer?: {id?: string} | null} | null} | null | undefined,
  profile: {id?: string; userId?: string} | null | undefined,
): boolean =>
  Boolean(
    certificate?.transfer &&
      IN_FLIGHT_TRANSFER_RECORD_STATUSES.includes(
        certificate.transfer.status as (typeof IN_FLIGHT_TRANSFER_RECORD_STATUSES)[number],
      ) &&
      isIncomingTransferFor(certificate.transfer.toCustomer, profile),
  );

/**
 * VRG-486 — is this transfer a gift whose sender asked not to be named?
 *
 * The SERVER is what makes the promise real: for an anonymous gift it withholds
 * `transfer.fromCustomer` and `metadata.giftFromName` from every reader but the
 * sender (`CertificateDataAggregate.redactAnonymousGiftSender`). So a client can
 * no longer name the sender even if it wanted to, and this predicate exists only
 * to decide what to render INSTEAD.
 *
 * That distinction is why it keys on `metadata.isAnonymous` rather than on
 * `!fromCustomer`. An absent sender has two meanings on this payload and they need
 * opposite treatments: a `primary` transfer has no `fromCustomer` because the
 * sender is the BRAND, and `StatusNotification` reads exactly that absence to swap
 * in the company's name and logo. Left to infer from absence alone, a redacted
 * anonymous gift would have been rendered as "A gift from <Brand>" — a confident,
 * wrong attribution, which is worse than the leak it replaced.
 *
 * Shared rather than inlined for the reason `isIncomingTransferFor` is (§36): three
 * surfaces ask this — the certificates-list row, the gift-in card, and the decline
 * confirmation — and a fourth inline copy is how they start disagreeing.
 */
export const isAnonymousGift = (
  transfer:
    | {type?: string | null; metadata?: {isAnonymous?: boolean} | null}
    | null
    | undefined,
): boolean =>
  transfer?.type === 'gift' && transfer?.metadata?.isAnonymous === true;

/**
 * What an anonymous sender is CALLED, in one place so the list row, the card and
 * the decline copy cannot drift into "Anonymous" / "Anonymous User" / "Someone".
 *
 * Sentence-case "Anonymous" rather than the email path's "Anonymous User": this
 * label sits where a person's NAME goes, beside their avatar, and "Anonymous User"
 * reads as a system account. The email's copy is a separate surface with its own
 * constraints and is deliberately not refactored from here.
 */
export const ANONYMOUS_SENDER_LABEL = 'Anonymous';

/**
 * The certificate's own short identifier, for a list sub-line.
 *
 * VRG-231 — this exists because the sub-line used to fall back to the CHIP UID
 * (`tag_uid`, and its since-removed `e_tag_id` alias) when a product carried no SKU. The
 * chip UID is a physical part identifier and is never shown to a customer, so the fallback
 * had to become something else rather than simply disappear: with no fallback at all, two
 * identical items in the same list render an identical row and become indistinguishable —
 * which is the exact case the sub-line is there for.
 *
 * The certificate id is the right replacement because it is stable, unique per row, and
 * the value a customer can quote to support. Truncated to 8 characters because a full uuid
 * is unreadable at label size and the prefix is already unique within one person's list.
 *
 * Callers render this ONLY as a fallback — a real SKU is more meaningful and wins.
 */
export const shortCertificateId = (id: string): string => String(id ?? '').slice(0, 8)

/**
 * What a certificate list row shows beneath its title: the product SKU when there is one,
 * otherwise the certificate's own short id. Never a chip UID, and never empty.
 */
export const certificateSubLabel = (cert: {product?: {sku?: string | null} | null; id?: string}): string =>
  cert?.product?.sku || `Certificate ${shortCertificateId(cert?.id ?? '')}`

/**
 * Does `viewerId` own this certificate?
 *
 * ## The bug this exists to end
 *
 * Six surfaces hand-rolled this as:
 *
 *     certificate?.customer
 *       ? certificate?.customer?.id === profile?.id
 *       : certificate?.company?.id === profile?.id
 *
 * On a PUBLIC certificate viewed by a stranger there is no profile, so
 * `profile?.id` is `undefined`. For a brand-held certificate (no customer) the
 * company branch ran, and `company?.id` was ALSO `undefined` — because the
 * anonymous payload carried no company at all. `undefined === undefined` is
 * `true`, so an anonymous scanner was classified as the OWNER of every
 * brand-held certificate they tapped.
 *
 * Two independent absences coinciding is what made it invisible: each side
 * looked like an ordinary optional-chain, and the bug only appears when BOTH
 * are missing at once.
 *
 * ## The fix, and why it is the viewer and not the payload
 *
 * A missing viewer is the decisive fact: nobody is not somebody. So the guard
 * is `if (!viewerId) return false`, which holds no matter what the payload
 * carries. Fixing this by making the server always send `company.id` would
 * work today and silently regress the moment any projection narrowed again —
 * and the certificate payload has narrowed twice already.
 *
 * Extracted rather than patched in place (§36): six copies is five too many,
 * and a shared helper is what stops the seventh surface re-deriving it.
 */
export const isCertificateOwnedBy = (
  certificate:
    | {
        customer?: {id?: string | null} | null
        company?: {id?: string | null} | null
      }
    | null
    | undefined,
  viewerId: string | null | undefined,
): boolean => {
  // Anonymous: no identity, so no ownership. This single line is the fix.
  if (!viewerId) return false
  if (!certificate) return false

  // `customer` PRESENT means the certificate is owned by a person, and the
  // company is then irrelevant — the original branch, preserved.
  return certificate.customer
    ? certificate.customer.id === viewerId
    : certificate.company?.id === viewerId
}
